import JSZip from 'jszip';
import { logError, logInfo } from '../logger';
import { calculateRouteDistance } from '../routeHelpers';

/**
 * Extracts layer information from GeoJSON properties
 * @param {Object} properties - Feature properties
 * @returns {string} - Layer name
 */
const extractLayerInfo = (properties) => {
  // Try to find layer information in properties
  if (properties) {
    // Check common layer property names
    const layerProps = [
      'layer', 'layerName', 'group', 'folder', 'collection', 
      'category', 'type', 'class', 'classification'
    ];
    
    for (const prop of layerProps) {
      if (properties[prop]) {
        return properties[prop];
      }
    }
  }
  
  return 'default';
};

export const parseGeoJSON = async (data) => {
  try {
    // Validate input is an object
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid GeoJSON: Input must be an object');
    }

    // Check if it's a FeatureCollection
    if (data.type !== 'FeatureCollection' || !Array.isArray(data.features)) {
      throw new Error('Invalid GeoJSON: Must be a FeatureCollection');
    }

    const points = [];
    const routes = [];
    const layers = new Set();

    // Process each feature
    for (const feature of data.features) {
      if (!feature.geometry || !feature.geometry.type) {
        continue;
      }

      const layer = extractLayerInfo(feature.properties);
      layers.add(layer);

      switch (feature.geometry.type) {
        case 'Point':
          points.push({
            coordinates: feature.geometry.coordinates,
            properties: feature.properties || {},
            layer
          });
          break;

        case 'LineString':
          routes.push({
            coordinates: feature.geometry.coordinates,
            properties: feature.properties || {},
            layer,
            distance: calculateRouteDistance(feature.geometry.coordinates)
          });
          break;

        // Handle other geometry types as needed
      }
    }

    return {
      points,
      routes,
      layers: Array.from(layers)
    };
  } catch (error) {
    throw new Error(`Failed to parse GeoJSON: ${error.message}`);
  }
};

/**
 * Process a ZIP file containing GeoJSON and images
 * @param {File} file - ZIP file
 * @returns {Promise<Object>} - Extracted data
 */
export const processZipWithImages = async (file) => {
  try {
    logInfo(`Processing ZIP file: ${file.name}, size: ${file.size} bytes`);
    
    // Load the ZIP file
    const zip = new JSZip();
    const zipContents = await zip.loadAsync(file);
    
    // Find GeoJSON files
    const geojsonFiles = Object.keys(zipContents.files).filter(
      filename => filename.toLowerCase().endsWith('.geojson')
    );
    
    // Find image files
    const imageFiles = Object.keys(zipContents.files).filter(
      filename => {
        const lowerFilename = filename.toLowerCase();
        return lowerFilename.endsWith('.jpg') || 
               lowerFilename.endsWith('.jpeg') || 
               lowerFilename.endsWith('.png') || 
               lowerFilename.endsWith('.gif');
      }
    );
    
    // Find metadata file
    const metaFile = Object.keys(zipContents.files).find(
      filename => filename.toLowerCase() === 'meta.json'
    );
    
    logInfo(`Found ${geojsonFiles.length} GeoJSON files, ${imageFiles.length} images, and ${metaFile ? 1 : 0} metadata files`);
    
    // Process metadata if available
    let metadata = {};
    if (metaFile) {
      try {
        const metaContent = await zipContents.files[metaFile].async('text');
        metadata = JSON.parse(metaContent);
        logInfo('Metadata parsed successfully');
      } catch (error) {
        logError('Failed to parse metadata:', error);
      }
    }
    
    // Process GeoJSON files
    let points = [];
    let routes = [];
    let layers = new Set();
    
    for (const geojsonFile of geojsonFiles) {
      try {
        const content = await zipContents.files[geojsonFile].async('text');
        const data = JSON.parse(content);
        
        // Validate GeoJSON
        const validation = validateGeoJSON(data);
        if (!validation.isValid) {
          logError(`Invalid GeoJSON in ${geojsonFile}:`, validation.error);
          continue;
        }
        
        // Parse GeoJSON
        const result = await parseGeoJSON(data);
        routes = [...routes, ...result.routes];
        result.layers.forEach(layer => layers.add(layer));
        
        logInfo(`Parsed ${result.points.length} points and ${result.routes.length} routes from ${geojsonFile}`);
      } catch (error) {
        logError(`Failed to parse ${geojsonFile}:`, error);
      }
    }
    
    // Process images
    const images = [];
    const imagesBySize = {};
    for (const imageFile of imageFiles) {
      try {
        const imageBlob = await zipContents.files[imageFile].async('blob');
        const imageUrl = URL.createObjectURL(imageBlob);
        
        // Get image dimensions
        try {
          const dimensions = await getImageDimensions(new File([imageBlob], imageFile.split('/').pop()));
          const dimensionKey = `${dimensions.width}x${dimensions.height}`;
          
          // Extract potential POI ID from filename
          // Assuming format like "poi_123.jpg" or "123_photo.jpg"
          const filename = imageFile.split('/').pop();
          const poiIdMatch = filename.match(/(?:poi_|^)([a-zA-Z0-9-_]+)(?:_photo|\.)/i);
          const poiId = poiIdMatch ? poiIdMatch[1] : null;
          
          // Store image by dimensions
          if (!imagesBySize[poiId]) {
            imagesBySize[poiId] = {};
          }
          
          imagesBySize[poiId][dimensionKey] = {
            id: `img_${Math.random().toString(36).substring(2, 9)}`,
            name: filename,
            data: imageUrl,
            poiId,
            dimensions: dimensionKey
          };
          
          logInfo(`Processed image: ${filename} (${dimensionKey})`);
        } catch (dimError) {
          logError(`Failed to get dimensions for ${imageFile}:`, dimError);
          
          // Still add the image without dimensions
          const filename = imageFile.split('/').pop();
          const poiIdMatch = filename.match(/(?:poi_|^)([a-zA-Z0-9-_]+)(?:_photo|\.)/i);
        }
        // Extract potential POI ID from filename
        // Assuming format like "poi_123.jpg" or "123_photo.jpg"
        const filename = imageFile.split('/').pop();
        const poiIdMatch = filename.match(/(?:poi_|^)([a-zA-Z0-9-_]+)(?:_photo|\.)/i);
        const poiId = poiIdMatch ? poiIdMatch[1] : null;
        
        // Add image to the images array
        images.push({
          id: `img_${Math.random().toString(36).substring(2, 9)}`,
          name: filename,
          data: imageUrl,
          poiId
        });
        
        logInfo(`Processed image: ${filename}`);
      } catch (error) {
        logError(`Failed to process image ${imageFile}:`, error);
      }
    }
    
    // Try to associate images with POIs based on metadata or naming conventions
    if (metadata.imageAssociations) {
      for (const image of images) {
        const association = metadata.imageAssociations.find(a => a.imageName === image.name);
        if (association) {
          image.poiId = association.poiId;
        }
      }
    }
    
    return {
      points,
      routes,
      layers: Array.from(layers),
      images,
      metadata
    };
  } catch (error) {
    logError('Failed to process ZIP file:', error);
    throw new Error(`Failed to process ZIP file: ${error.message}`);
  }
};